import { RefObject } from "react";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListParamsType } from "../../../../../../list/services/types";
import { ListConfigAction } from "../../../services/types";
import { ActionsMenu } from "../ActionsMenu";
import { SingleActionButton } from "../buttons/SingleActionButton";

interface ActionsListProps {
	mainDivRef: RefObject<HTMLDivElement>;
	actions: ListConfigAction[];
	item: any;
	params?: ListParamsType;
	fetchData?: (params: ListParamsType) => Promise<any>;
}

export interface SingleActionListProps {
	mainDivRef: RefObject<HTMLDivElement>;
	actions?: ListConfigAction[];
	item: any;
	doesIdColumnRedirectToPreviewPage?: boolean;
	params?: ListParamsType;
	fetchData?: (params: ListParamsType) => Promise<any>;
}

const ActionsList = ({ mainDivRef, actions, item, params, fetchData }: ActionsListProps): JSX.Element => {
	const { addFlash } = useFlash();
	const confirmation = useConfirmation();
	const { t } = useTranslation();

	return (
		<ActionsMenu mainDivRef={mainDivRef}>
			{actions.map((action) => {
				if (action.link) {
					return (
						<Dropdown.Item key={action.name} as={Link} to={action.link(item)}>
							{action.name}
						</Dropdown.Item>
					);
				}
				if (action.click) {
					const onClick = async () => {
						try {
							if (action.confirmation?.use) {
								await confirmation({
									title: action.confirmation?.title ?? t("lib:confirmation.title"),
									message: action.confirmation?.message ?? t("lib:confirmation.message.action"),
								});
							}
							await action.click?.(item, params);
							if (action.refreshListAfter && fetchData) {
								await fetchData(params ?? { search: "" });
							}
						} catch (err) {
							if (action.customErrorHandler) {
								action.customErrorHandler(err, item);
							} else {
								handleError.alert(err, addFlash);
							}
						}
					};

					return (
						<Dropdown.Item key={action.name} onClick={onClick}>
							{action.name}
						</Dropdown.Item>
					);
				}

				return null;
			})}
		</ActionsMenu>
	);
};

export const SingleActionList = ({
	mainDivRef,
	actions,
	item,
	doesIdColumnRedirectToPreviewPage,
	params,
	fetchData,
}: SingleActionListProps): JSX.Element | null => {
	if (!actions || actions.length === 0) return null;

	if (doesIdColumnRedirectToPreviewPage) {
		return (
			<ActionsList mainDivRef={mainDivRef} actions={actions} item={item} params={params} fetchData={fetchData} />
		);
	}

	const moreActions = actions.slice(1);

	return (
		<ButtonGroup className="actions-button-group">
			<SingleActionButton action={actions[0]} item={item} params={params} fetchData={fetchData} />
			{moreActions.length > 0 && (
				<ActionsList
					mainDivRef={mainDivRef}
					actions={moreActions}
					item={item}
					params={params}
					fetchData={fetchData}
				/>
			)}
		</ButtonGroup>
	);
};
